import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { X, Plus, Minus } from "lucide-react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { fetchScheduleAttendanceTags, getDepartments, getJobCodes, getSitesV2, searchEmployee } from "../../redux/features/common/api";
import { EmployeeSearchResult } from "../../types/response";
import { format } from "date-fns";
import { toast } from "react-toastify";
import ColorPickerWithPalette from "../common/ColorPickerWithPalette";
import { SchedulingToolUtils } from "../../utils/schedulingToolConfig";

interface ShiftRow {
    id: string;
    shift_code: string;
    shift_date: string;
    positions: number | string; // Allow string temporarily for better UX during editing
    start_datetime: string;
    end_datetime: string;
}

interface CreateOpenShiftModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    isSubmitting?: boolean;
}

const CreateOpenShiftModal: React.FC<CreateOpenShiftModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting = false,
}) => {
    const dispatch = useAppDispatch();
    const { shifts, departments, sitesV2, jobCodes, attendanceTags } = useSelector((state: RootState) => state.common);
    const user = useSelector((state: RootState) => state.user.user);
    const schedulingConfig = SchedulingToolUtils.getSchedulingConfig(user);
    const allowCustomShift = !schedulingConfig.RESTRICT_TO_PREDEFINED_SHIFTS;
    
    // Initialize with one empty shift row
    const [shiftRows, setShiftRows] = useState<ShiftRow[]>([
        {
            id: '1',
            shift_code: '',
            shift_date: format(new Date(), 'yyyy-MM-dd'),
            positions: 1,
            start_datetime: '',
            end_datetime: '',
        }
    ]);
    
    const [selectedEmployees, setSelectedEmployees] = useState<EmployeeSearchResult[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<any[]>([]);
    const [whoCanTakeShift, setWhoCanTakeShift] = useState<"all" | "specific">("all");
    const [showSpecificEligibility, setShowSpecificEligibility] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const prevIsOpenRef = useRef<boolean>(false);

    // shift_attributes (optional)
    const [shiftNotes, setShiftNotes] = useState("");
    const [selectedSiteCode, setSelectedSiteCode] = useState<string>("");
    const [selectedDepartmentCode, setSelectedDepartmentCode] = useState<string>("");
    const [selectedWorkCode, setSelectedWorkCode] = useState<string>("");
    const [shiftColor, setShiftColor] = useState<string>("");
    const [showShiftAttributes, setShowShiftAttributes] = useState(false);
    const [selectedAttendanceTags, setSelectedAttendanceTags] = useState<Record<string, { value_code: string; value_text: string }[]>>(
        {}
    );
    const didFetchReferenceDataRef = useRef(false);
    
    // Mock groups data (until we have real groups in Redux)
    const mockGroups = useMemo(() => [
        { value: "testgroup", label: "Test Group" },
        { value: "group123", label: "Group 123" },
        { value: "group456", label: "Group 456" }
    ], []);
    
    // Format department options for react-select
    const departmentOptions = useMemo(() => 
        departments.map((dept: any) => ({
            value: dept.department_code || dept.code || '',
            label: dept.department_name || dept.name || ''
        })),
        [departments]
    );

    const siteOptions = useMemo(() => {
        const list = Array.isArray(sitesV2) ? sitesV2 : [];
        return list
            .map((s: any) => ({
                value: s.us_site_code || s.user_site_name || s.site_code || "",
                label: s.user_site_name || s.us_site_name || s.site_name || s.us_site_code || s.site_code || "",
            }))
            .filter((o: any) => o.value);
    }, [sitesV2]);

    const workCodeOptions = useMemo(() => {
        const list = Array.isArray(jobCodes) ? jobCodes : [];
        return list
            .map((j: any) => ({
                value: j.work_code_number || j.work_code || j.os_work_codes_id || "",
                label: j.work_code_name || j.name || j.work_code_number || j.work_code || "",
            }))
            .filter((o: any) => o.value);
    }, [jobCodes]);

    const attendanceTagsList = useMemo(() => (Array.isArray(attendanceTags) ? attendanceTags : []), [attendanceTags]);

    const getAttendanceTagOptions = useCallback((tag: any): { value_code: string; value_text: string }[] => {
        if (!tag?.field_json) return [];
        if (Array.isArray(tag.field_json)) {
            return tag.field_json.map((item: any) => ({
                value_code: item.field_value_code || item.value_code || item.code || "",
                value_text: item.field_value_text || item.value_text || item.text || item.name || "",
            }));
        }
        if (typeof tag.field_json === "string") {
            try {
                const arr = JSON.parse(tag.field_json);
                if (Array.isArray(arr)) {
                    return arr.map((item: any) => ({
                        value_code: item.field_value_code || item.value_code || item.code || "",
                        value_text: item.field_value_text || item.value_text || item.text || item.name || "",
                    }));
                }
            } catch {
                return [];
            }
        }
        return [];
    }, []);

    const handleAttendanceFieldChange = useCallback(
        (fieldCode: string, value: any) => {
            setSelectedAttendanceTags((prev) => ({
                ...prev,
                [fieldCode]: value,
            }));
        },
        []
    );
    
    // Format shift options for react-select with CUSTOM as first option when allowed
    const shiftOptions = useMemo(() => {
        const customOption = {
            value: 'CUSTOM_NGW_0001',
            label: 'CUSTOM SHIFT',
            shift_code: 'CUSTOM_NGW_0001',
            start_time: '',
            end_time: '',
        };
        
        const regularShifts = shifts.map((shift: any) => ({
            value: shift.os_schedules_site_shifts_id || shift.shift_code || '',
            label: shift.os_shift_name || shift.shift_name || shift.name || '',
            shift_code: shift.os_schedules_site_shifts_id || shift.shift_code || '',
            start_time: shift.os_shift_start_time || shift.start_time || '',
            end_time: shift.os_shift_end_time || shift.end_time || '',
        }));
        
        return allowCustomShift ? [customOption, ...regularShifts] : regularShifts;
    }, [allowCustomShift, shifts]);
    
    // Employee search function for AsyncSelect
    const loadEmployeeOptions = useCallback(async (inputValue: string) => {
        if (inputValue.length < 3) {
            return [];
        }
        
        try {
            const response = await dispatch(searchEmployee({
                search: inputValue,
                departments: selectedDepartments.map(d => d.value).join(','),
                groups: selectedGroups.map(g => g.value).join(','),
                site_id: '',
                page: 1
            })).unwrap();
            
            // Filter out already selected employees
            const selectedIds = selectedEmployees.map(emp => emp.os_emp_account_id);
            return response.data.employees
                .filter((employee: EmployeeSearchResult) => !selectedIds.includes(employee.os_emp_account_id))
                .map((employee: EmployeeSearchResult) => ({
                    value: employee,
                    label: employee.text
                }));
        } catch (error) {
            console.error('Error searching employees:', error);
            return [];
        }
    }, [dispatch, selectedDepartments, selectedGroups, selectedEmployees]);
    
    // Add a new shift row
    const handleAddShiftRow = () => {
        const newRow: ShiftRow = {
            id: Date.now().toString(),
            shift_code: '',
            shift_date: format(new Date(), 'yyyy-MM-dd'),
            positions: 1,
            start_datetime: '',
            end_datetime: '',
        };
        setShiftRows([...shiftRows, newRow]);
    };
    
    // Remove a shift row
    const handleRemoveShiftRow = (id: string) => {
        if (shiftRows.length > 1) {
            setShiftRows(shiftRows.filter(row => row.id !== id));
        }
    };
    
    // Update a shift row
    const handleUpdateShiftRow = (id: string, field: keyof ShiftRow, value: any) => {
        setShiftRows(shiftRows.map(row => 
            row.id === id ? { ...row, [field]: value } : row
        ));
    };
    
    // Handle shift selection and auto-fill datetime
    const handleShiftSelect = (id: string, selectedOption: any) => {
        const row = shiftRows.find(r => r.id === id);
        if (!row) return;
        
        const shiftCode = selectedOption?.shift_code || '';
        if (shiftCode === 'CUSTOM_NGW_0001' && !allowCustomShift) {
            toast.error('Please select a shift template.');
            return;
        }
        
        // Update shift code
        handleUpdateShiftRow(id, 'shift_code', shiftCode);
        
        // If CUSTOM, clear the datetimes to allow manual entry
        if (shiftCode === 'CUSTOM_NGW_0001') {
            setShiftRows(shiftRows.map(r => 
                r.id === id 
                    ? { ...r, shift_code: shiftCode, start_datetime: '', end_datetime: '' }
                    : r
            ));
            return;
        }
        
        // Auto-fill start and end datetime if date is selected and shift has times
        if (row.shift_date && selectedOption?.start_time && selectedOption?.end_time) {
            const startDatetime = `${row.shift_date} ${selectedOption.start_time}`;
            const endDatetime = `${row.shift_date} ${selectedOption.end_time}`;
            setShiftRows(shiftRows.map(r => 
                r.id === id 
                    ? { ...r, shift_code: shiftCode, start_datetime: startDatetime, end_datetime: endDatetime }
                    : r
            ));
        }
    };
    
    // Handle date change and update datetime
    const handleDateChange = (id: string, date: string) => {
        const row = shiftRows.find(r => r.id === id);
        if (!row) return;
        
        // Validate that the date is not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(date);
        selectedDate.setHours(0, 0, 0, 0);
        
        // If the selected date is in the past, use today's date instead
        if (selectedDate < today) {
            date = format(today, 'yyyy-MM-dd');
        }
        
        handleUpdateShiftRow(id, 'shift_date', date);
        
        // If CUSTOM is selected, update the date portion of existing datetime values
        if (row.shift_code === 'CUSTOM_NGW_0001') {
            const updatedRows = shiftRows.map(r => {
                if (r.id !== id) return r;
                let newStartDatetime = r.start_datetime;
                let newEndDatetime = r.end_datetime;
                
                // If start_datetime exists, update its date portion (format: YYYY-MM-DD HH:mm:ss)
                if (r.start_datetime) {
                    const parts = r.start_datetime.split(' ');
                    if (parts.length >= 2) {
                        const time = parts[1]; // Get time portion (HH:mm:ss or HH:mm)
                        newStartDatetime = `${date} ${time}`;
                    }
                }
                
                // If end_datetime exists, update its date portion
                if (r.end_datetime) {
                    const parts = r.end_datetime.split(' ');
                    if (parts.length >= 2) {
                        const time = parts[1]; // Get time portion (HH:mm:ss or HH:mm)
                        newEndDatetime = `${date} ${time}`;
                    }
                }
                
                return { ...r, shift_date: date, start_datetime: newStartDatetime, end_datetime: newEndDatetime };
            });
            setShiftRows(updatedRows);
            return;
        }
        
        // Update datetimes if shift is selected (but not CUSTOM)
        const selectedShift = shiftOptions.find(s => s.shift_code === row.shift_code);
        if (selectedShift && selectedShift.shift_code !== 'CUSTOM_NGW_0001' && selectedShift?.start_time && selectedShift?.end_time) {
            const startDatetime = `${date} ${selectedShift.start_time}`;
            const endDatetime = `${date} ${selectedShift.end_time}`;
            setShiftRows(shiftRows.map(r => 
                r.id === id 
                    ? { ...r, shift_date: date, start_datetime: startDatetime, end_datetime: endDatetime }
                    : r
            ));
        }
    };
    
    // Handle custom time changes (time only, date comes from shift_date field)
    const handleCustomTimeChange = (id: string, type: 'start' | 'end', timeValue: string) => {
        const row = shiftRows.find(r => r.id === id);
        if (!row || row.shift_code !== 'CUSTOM_NGW_0001') return;
        
        // Merge date from shift_date with time value to create MySQL datetime format
        if (row.shift_date && timeValue) {
            const mysqlDatetime = `${row.shift_date} ${timeValue}:00`;
            if (type === 'start') {
                handleUpdateShiftRow(id, 'start_datetime', mysqlDatetime);
            } else {
                handleUpdateShiftRow(id, 'end_datetime', mysqlDatetime);
            }
        } else {
            // If no date, just clear the datetime
            if (type === 'start') {
                handleUpdateShiftRow(id, 'start_datetime', '');
            } else {
                handleUpdateShiftRow(id, 'end_datetime', '');
            }
        }
    };
    
    const handleEmployeeSelect = (selected: any) => {
        if (selected?.value) {
            const newEmployee = selected.value;
            const updatedEmployees = [...selectedEmployees, newEmployee];
            setSelectedEmployees(updatedEmployees);
            setSearchInput("");
        }
    };
    
    const handleRemoveEmployee = (employeeId: string) => {
        setSelectedEmployees(selectedEmployees.filter(emp => emp.os_emp_account_id !== employeeId));
    };
    
    const handleDepartmentChange = (selected: any) => {
        setSelectedDepartments(selected || []);
    };
    
    const handleGroupChange = (selected: any) => {
        setSelectedGroups(selected || []);
    };
    
    // Reset form function
    const resetForm = useCallback(() => {
        setShiftRows([{
            id: '1',
            shift_code: '',
            shift_date: format(new Date(), 'yyyy-MM-dd'),
            positions: 1,
            start_datetime: '',
            end_datetime: '',
        }]);
        setSelectedEmployees([]);
        setSelectedDepartments([]);
        setSelectedGroups([]);
        setSearchInput("");
        setShiftNotes("");
        setSelectedSiteCode("");
        setSelectedDepartmentCode("");
        setSelectedWorkCode("");
        setShiftColor("");
        setShowShiftAttributes(false);
        setSelectedAttendanceTags({});
    }, []);
    
    // Reset form when modal closes (after being open)
    useEffect(() => {
        // Only reset if modal was open and is now closed
        if (prevIsOpenRef.current && !isOpen) {
            resetForm();
            didFetchReferenceDataRef.current = false;
        }
        // Update the ref for next render
        prevIsOpenRef.current = isOpen;
    }, [isOpen, resetForm]);

    // Ensure reference data is loaded (sites, job codes, departments, attendance tags)
    useEffect(() => {
        if (!isOpen) return;
        if (didFetchReferenceDataRef.current) return;
        didFetchReferenceDataRef.current = true;

        // Scheduling tool loads these on mount; the open shift modal may open without them
        const employeeId = String(user?.os_emp_account_id ?? "");
        if (employeeId) {
            dispatch(getSitesV2({ employee_id: employeeId } as any));
        }
        dispatch(getJobCodes());
        dispatch(getDepartments());
        dispatch(fetchScheduleAttendanceTags());
    }, [isOpen, dispatch, user?.os_emp_account_id]);
    
    const handleSubmit = () => {
        if (!allowCustomShift && shiftRows.some(row => row.shift_code === 'CUSTOM_NGW_0001')) {
            toast.error('Please select a shift template.');
            return;
        }

        // Validate positions - max 100 per record
        const invalidPositions = shiftRows.filter(row => {
            const positions = typeof row.positions === 'string' ? parseInt(row.positions, 10) : row.positions;
            return isNaN(positions) || positions < 1 || positions > 100;
        });
        
        if (invalidPositions.length > 0) {
            toast.error('Positions must be between 1 and 100 per record.');
            return;
        }
        
        // Build request body according to the specified format
        const shiftAttributesAttendanceTags = Object.entries(selectedAttendanceTags)
            .map(([field_code, values]) => ({
                field_code,
                field_values: Array.isArray(values) ? values : [],
            }))
            .filter((x) => x.field_values.length > 0);

        const requestBody = {
            shifts: shiftRows.map(row => {
                // For CUSTOM shifts, ensure datetime is properly formatted (merge date and time)
                let startDatetime = row.start_datetime;
                let endDatetime = row.end_datetime;
                
                if (row.shift_code === 'CUSTOM_NGW_0001') {
                    // If we have date and time separately, merge them into MySQL format
                    if (row.shift_date) {
                        // Extract time from datetime if exists, or use current time if not set
                        if (row.start_datetime) {
                            const parts = row.start_datetime.split(' ');
                            if (parts.length >= 2) {
                                startDatetime = `${row.shift_date} ${parts[1]}`;
                            } else {
                                // If only time format (HH:mm), add seconds
                                startDatetime = `${row.shift_date} ${row.start_datetime}:00`;
                            }
                        }
                        
                        if (row.end_datetime) {
                            const parts = row.end_datetime.split(' ');
                            if (parts.length >= 2) {
                                endDatetime = `${row.shift_date} ${parts[1]}`;
                            } else {
                                // If only time format (HH:mm), add seconds
                                endDatetime = `${row.shift_date} ${row.end_datetime}:00`;
                            }
                        }
                    }
                }
                
                return {
                    shift_code: row.shift_code,
                    shift_date: row.shift_date,
                    positions: typeof row.positions === 'string' ? parseInt(row.positions, 10) || 1 : row.positions,
                    start_datetime: startDatetime,
                    end_datetime: endDatetime,
                };
            }),
            employee_ids: whoCanTakeShift === "specific" ? selectedEmployees.map(emp => emp.os_emp_account_id) : [],
            department_codes: whoCanTakeShift === "specific" ? selectedDepartments.map(dept => dept.value) : [],
            group_codes: whoCanTakeShift === "specific" ? selectedGroups.map(group => group.value) : [],
            type: whoCanTakeShift === "specific" ? "filtered" : "all",
            shift_attributes: showShiftAttributes
                ? {
                      notes: shiftNotes.trim() || undefined,
                      site_code: selectedSiteCode || undefined,
                      department_code: selectedDepartmentCode || undefined,
                      work_code: selectedWorkCode || undefined,
                      shift_color: shiftColor || undefined,
                      attendance_tags:
                          shiftAttributesAttendanceTags.length > 0
                              ? shiftAttributesAttendanceTags.map((tag) => {
                                    if (tag.field_values.length === 0) {
                                        return { code: tag.field_code, value: "" };
                                    }
                                    if (tag.field_values.length === 1) {
                                        const v = tag.field_values[0];
                                        return {
                                            code: tag.field_code,
                                            value: v.value_text || v.value_code || "",
                                        };
                                    }
                                    return {
                                        code: tag.field_code,
                                        value: tag.field_values.map((v) => v.value_text || v.value_code || ""),
                                    };
                                })
                              : undefined,
                  }
                : undefined,
        };
        
        onSubmit(requestBody);
    };
    
    const handleCancel = () => {
        resetForm();
        onClose();
    };
    
    if (!isOpen) return null;
    
    // Custom styles for React Select to fix z-index issues
    const customSelectStyles = {
        control: (provided: any) => ({
            ...provided,
            minHeight: '38px',
        }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 9999,
        }),
        menuPortal: (provided: any) => ({
            ...provided,
            zIndex: 9999,
        }),
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400] p-0 md:p-4 overflow-y-auto">
            <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full max-w-5xl h-full md:h-auto md:max-h-[90vh] flex flex-col my-0 md:my-4">
                {/* Header */}
                <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex-shrink-0">
                    <h2 className="text-base md:text-lg lg:text-xl font-bold text-gray-900">Post Open Shift</h2>
                    <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    >
                        <X size={20} className="md:w-6 md:h-6" />
                    </button>
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6">
                    {/* Shift Details Section */}
                    <div className="mb-6 md:mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                            <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center">
                                <div className="w-1 h-5 md:h-6 bg-blue-500 mr-2 md:mr-3"></div>
                                Shift Details
                            </h3>
                            <button
                                onClick={handleAddShiftRow}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors self-start sm:self-auto"
                            >
                                <Plus size={16} />
                                <span className="hidden sm:inline">Add Shift</span>
                                <span className="sm:hidden">Add</span>
                            </button>
                        </div>
                        
                        {shiftRows.map((row, index) => (
                            <div key={row.id} className="mb-4 pb-4 border-b border-gray-100 last:border-b-0">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                                    {/* Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Date
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={row.shift_date}
                                                min={format(new Date(), 'yyyy-MM-dd')}
                                                onChange={(e) => handleDateChange(row.id, e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Shift */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Shift Template
                                        </label>
                                        <Select
                                            value={shiftOptions.find(opt => opt.shift_code === row.shift_code) || null}
                                            onChange={(selected) => handleShiftSelect(row.id, selected)}
                                            options={shiftOptions}
                                            placeholder="--Select Shift--"
                                            isClearable
                                            isSearchable
                                            className="text-sm"
                                            classNamePrefix="react-select"
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                    
                                    {/* Positions */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Number of employees needed
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="1"
                                                max="100"
                                                value={row.positions === '' ? '' : row.positions}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Allow empty string for backspacing - user can clear and type new number
                                                    if (value === '') {
                                                        handleUpdateShiftRow(row.id, 'positions', '');
                                                    } else {
                                                        const numValue = parseInt(value, 10);
                                                        // Only update if it's a valid positive number and doesn't exceed max
                                                        if (!isNaN(numValue) && numValue > 0 && numValue <= 100) {
                                                            handleUpdateShiftRow(row.id, 'positions', numValue);
                                                        } else if (!isNaN(numValue) && numValue > 100) {
                                                            // If exceeds max, set to max value
                                                            handleUpdateShiftRow(row.id, 'positions', 100);
                                                        }
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    // Ensure value is between 1 and 100 when field loses focus
                                                    const value = e.target.value;
                                                    const numValue = parseInt(value, 10);
                                                    if (value === '' || isNaN(numValue) || numValue < 1) {
                                                        handleUpdateShiftRow(row.id, 'positions', 1);
                                                    } else if (numValue > 100) {
                                                        handleUpdateShiftRow(row.id, 'positions', 100);
                                                    }
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                            <button
                                                onClick={() => handleRemoveShiftRow(row.id)}
                                                disabled={shiftRows.length === 1}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Remove shift"
                                            >
                                                <Minus size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Custom Time Inputs - Show when CUSTOM is selected */}
                                {row.shift_code === 'CUSTOM_NGW_0001' && (
                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        {/* Start Time */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                Start Time <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={(() => {
                                                    if (!row.start_datetime) return '';
                                                    // Extract time from YYYY-MM-DD HH:mm:ss format
                                                    const parts = row.start_datetime.split(' ');
                                                    if (parts.length >= 2) {
                                                        return parts[1].slice(0, 5); // Return HH:mm
                                                    }
                                                    return '';
                                                })()}
                                                min={(() => {
                                                    // If end time exists and is earlier, set min to current time
                                                    const now = new Date();
                                                    const today = format(now, 'yyyy-MM-dd');
                                                    if (row.shift_date === today) {
                                                        // If it's today, set min to current time
                                                        return now.toTimeString().slice(0, 5);
                                                    }
                                                    return '';
                                                })()}
                                                onChange={(e) => {
                                                    const timeValue = e.target.value;
                                                    handleCustomTimeChange(row.id, 'start', timeValue);
                                                }}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            />
                                        </div>
                                        
                                        {/* End Time */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                End Time <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={(() => {
                                                    if (!row.end_datetime) return '';
                                                    // Extract time from YYYY-MM-DD HH:mm:ss format
                                                    const parts = row.end_datetime.split(' ');
                                                    if (parts.length >= 2) {
                                                        return parts[1].slice(0, 5); // Return HH:mm
                                                    }
                                                    return '';
                                                })()}
                                                min={(() => {
                                                    // Set min to start time if start time exists
                                                    if (row.start_datetime) {
                                                        const parts = row.start_datetime.split(' ');
                                                        if (parts.length >= 2) {
                                                            return parts[1].slice(0, 5); // Return HH:mm
                                                        }
                                                    }
                                                    return '';
                                                })()}
                                                onChange={(e) => {
                                                    const timeValue = e.target.value;
                                                    handleCustomTimeChange(row.id, 'end', timeValue);
                                                }}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    {/* Who can take this shift Section */}
                    <div>
                        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <div className="w-1 h-5 md:h-6 bg-blue-500 mr-2 md:mr-3"></div>
                            Who can take this shift
                        </h3>
                        
                        <div className="space-y-4 md:space-y-6">
                            {/* Who can take this shift */}
                            <div className="rounded-lg border border-gray-200 bg-white p-4">
                                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="whoCanTakeShift"
                                            checked={whoCanTakeShift === "all"}
                                            onChange={() => {
                                                setWhoCanTakeShift("all");
                                                setShowSpecificEligibility(false);
                                            }}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">All Employees</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="whoCanTakeShift"
                                            checked={whoCanTakeShift === "specific"}
                                            onChange={() => {
                                                setWhoCanTakeShift("specific");
                                                setShowSpecificEligibility(true);
                                            }}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Specific Employees</span>
                                    </label>
                                </div>
                            </div>

                            {whoCanTakeShift === "specific" && (
                                <div className="rounded-lg border border-gray-200 bg-white">
                                    <button
                                        type="button"
                                        onClick={() => setShowSpecificEligibility((v) => !v)}
                                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
                                    >
                                        <span>Eligibility filters</span>
                                        <span className="text-blue-600">{showSpecificEligibility ? "Hide" : "Show"}</span>
                                    </button>

                                    {showSpecificEligibility && (
                                        <div className="px-4 pb-4 space-y-4 md:space-y-6">
                            {/* Employees Search */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-medium text-gray-700">
                                        Employees
                                    </label>
                                    {selectedEmployees.length > 0 && (
                                        <button
                                            onClick={() => setSelectedEmployees([])}
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                        >
                                            Clear All
                                        </button>
                                    )}
                                </div>
                                
                                {/* Selected employees as chips */}
                                {selectedEmployees.length > 0 && (
                                    <div className="mb-3 flex flex-wrap gap-2">
                                        {selectedEmployees.map((employee) => (
                                            <div
                                                key={employee.os_emp_account_id}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                            >
                                                <span>{employee.text}</span>
                                                <button
                                                    onClick={() => handleRemoveEmployee(employee.os_emp_account_id)}
                                                    className="ml-2 text-blue-600 hover:text-blue-800"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                <AsyncSelect
                                    value={null}
                                    onChange={handleEmployeeSelect}
                                    loadOptions={loadEmployeeOptions}
                                    placeholder="Search for employees"
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue.length < 3 ? "Type at least 3 characters to search" : "No employees found"
                                    }
                                    className="text-sm"
                                    classNamePrefix="react-select"
                                    isClearable
                                    isSearchable
                                    inputValue={searchInput}
                                    onInputChange={setSearchInput}
                                    styles={customSelectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                {/* Departments */}
                                {departmentOptions.length > 0 && (
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="text-sm font-medium text-gray-700">
                                                Departments
                                            </label>
                                            {selectedDepartments.length > 0 && (
                                                <button
                                                    onClick={() => setSelectedDepartments([])}
                                                    className="text-xs text-blue-600 hover:text-blue-800"
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                        <Select
                                            isMulti
                                            value={selectedDepartments}
                                            onChange={handleDepartmentChange}
                                            options={departmentOptions}
                                            placeholder="Select Departments"
                                            className="text-sm"
                                            classNamePrefix="react-select"
                                            isSearchable
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                )}
                                
                                {/* Groups */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Groups
                                        </label>
                                        {selectedGroups.length > 0 && (
                                            <button
                                                onClick={() => setSelectedGroups([])}
                                                className="text-xs text-blue-600 hover:text-blue-800"
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                    <Select
                                        isMulti
                                        value={selectedGroups}
                                        onChange={handleGroupChange}
                                        options={mockGroups}
                                        placeholder="Select Groups"
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        isSearchable
                                        styles={customSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Optional Fields Section (bottom, like other sections) */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between gap-3 mb-4">
                            <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center">
                                <div className="w-1 h-5 md:h-6 bg-blue-500 mr-2 md:mr-3"></div>
                                Additional details (optional)
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowShiftAttributes((v) => !v)}
                                className="text-blue-500 hover:text-blue-600 text-sm font-medium flex items-center gap-1"
                            >
                                <Plus size={16} />
                                {showShiftAttributes ? "Hide" : "Show"}
                            </button>
                        </div>

                        {showShiftAttributes && (
                            <div className="border-t border-gray-200 pt-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                        <textarea
                                            value={shiftNotes}
                                            onChange={(e) => setShiftNotes(e.target.value)}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Notes for this open shift..."
                                        />
                                    </div>

                                    {siteOptions.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Site</label>
                                            <Select
                                                value={siteOptions.find((o: any) => o.value === selectedSiteCode) || null}
                                                onChange={(opt: any) => setSelectedSiteCode(opt?.value || "")}
                                                options={siteOptions}
                                                placeholder="Select site..."
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                styles={customSelectStyles}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                isClearable
                                            />
                                        </div>
                                    )}

                                    {departmentOptions.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                                            <Select
                                                value={departmentOptions.find((o: any) => o.value === selectedDepartmentCode) || null}
                                                onChange={(opt: any) => setSelectedDepartmentCode(opt?.value || "")}
                                                options={departmentOptions}
                                                placeholder="Select department..."
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                styles={customSelectStyles}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                isClearable
                                            />
                                        </div>
                                    )}

                                    {workCodeOptions.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Work code</label>
                                            <Select
                                                value={workCodeOptions.find((o: any) => o.value === selectedWorkCode) || null}
                                                onChange={(opt: any) => setSelectedWorkCode(opt?.value || "")}
                                                options={workCodeOptions}
                                                placeholder="Select work code..."
                                                className="text-sm"
                                                classNamePrefix="react-select"
                                                styles={customSelectStyles}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                isClearable
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <ColorPickerWithPalette
                                            value={shiftColor}
                                            onChange={(color) => setShiftColor(color)}
                                            label="Shift color"
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Attendance Tags:</h4>
                                        {attendanceTagsList.length === 0 ? (
                                            <div className="text-sm text-gray-500 py-2">No attendance tags available.</div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {attendanceTagsList.map((tag: any) => {
                                                    const fieldType = tag.field_type?.toLowerCase();
                                                    const isMultiselect = tag.multiselect === "true";
                                                    const options = getAttendanceTagOptions(tag);
                                                    const fieldValue = selectedAttendanceTags[tag.field_code] || [];
                                                    const isRequired = tag.required === "true";

                                                    if (fieldType === "dropdown" && options.length > 0) {
                                                        if (isMultiselect) {
                                                            const selectOptions = options.map((opt) => ({
                                                                value: opt.value_code,
                                                                label: opt.value_text,
                                                            }));
                                                            const selectedValues = Array.isArray(fieldValue)
                                                                ? fieldValue.map((v: any) => v.value_code || v)
                                                                : [];
                                                            const selectedOptions = selectOptions.filter((opt) =>
                                                                selectedValues.includes(opt.value)
                                                            );

                                                            return (
                                                                <div key={tag.field_code}>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                        {tag.field_name}
                                                                        {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                                    </label>
                                                                    <Select
                                                                        isMulti
                                                                        value={selectedOptions}
                                                                        onChange={(selected) => {
                                                                            const selectedArray = selected as Array<{ value: string; label: string }> | null;
                                                                            if (selectedArray) {
                                                                                const newValues = selectedArray.map((item) => ({
                                                                                    value_code: item.value,
                                                                                    value_text: item.label,
                                                                                }));
                                                                                handleAttendanceFieldChange(tag.field_code, newValues);
                                                                            } else {
                                                                                handleAttendanceFieldChange(tag.field_code, []);
                                                                            }
                                                                        }}
                                                                        options={selectOptions}
                                                                        placeholder={`Select ${tag.field_name}...`}
                                                                        className="text-sm"
                                                                        classNamePrefix="react-select"
                                                                        styles={customSelectStyles}
                                                                        menuPortalTarget={document.body}
                                                                        menuPosition="fixed"
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        const currentValue =
                                                            Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue[0].value_code : "";
                                                        return (
                                                            <div key={tag.field_code}>
                                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                    {tag.field_name}
                                                                    {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                                </label>
                                                                <select
                                                                    value={currentValue}
                                                                    onChange={(e) => {
                                                                        const selectedOpt = options.find((o: any) => o.value_code === e.target.value);
                                                                        if (selectedOpt) {
                                                                            handleAttendanceFieldChange(tag.field_code, [
                                                                                { value_code: selectedOpt.value_code, value_text: selectedOpt.value_text },
                                                                            ]);
                                                                        } else {
                                                                            handleAttendanceFieldChange(tag.field_code, []);
                                                                        }
                                                                    }}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                >
                                                                    <option value="">Select {tag.field_name}...</option>
                                                                    {options.map((opt: any) => (
                                                                        <option key={opt.value_code} value={opt.value_code}>
                                                                            {opt.value_text}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        );
                                                    }

                                                    const currentTextValue =
                                                        Array.isArray(fieldValue) && fieldValue.length > 0 ? fieldValue[0].value_text : "";
                                                    return (
                                                        <div key={tag.field_code}>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {tag.field_name}
                                                                {isRequired && <span className="text-red-500 ml-1">*</span>}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={currentTextValue}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    handleAttendanceFieldChange(
                                                                        tag.field_code,
                                                                        value ? [{ value_code: tag.field_code, value_text: value }] : []
                                                                    );
                                                                }}
                                                                placeholder={`Enter ${tag.field_name}`}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-end gap-2 md:gap-3 px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                    <button
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        className="px-4 md:px-6 py-2 md:py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={
                            isSubmitting || 
                            shiftRows.length === 0 || 
                            shiftRows.some(r => {
                                // Must have shift_code
                                if (!r.shift_code) return true;
                                // If CUSTOM, must have both start and end datetime (time merged with date)
                                if (r.shift_code === 'CUSTOM_NGW_0001') {
                                    return !r.shift_date || !r.start_datetime || !r.end_datetime;
                                }
                                // For regular shifts, datetimes should be auto-filled, but we don't require them here
                                // as they should already be set when shift is selected
                                return false;
                            })
                        }
                        className="px-4 md:px-6 py-2 md:py-2.5 bg-transparent border border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                        {isSubmitting ? 'Posting...' : <><span className="hidden sm:inline">Post Open Shift</span><span className="sm:hidden">Post</span></>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateOpenShiftModal;
